import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, WifiOff } from "lucide-react";
import TransactionCard from "@/components/TransactionCard";
import ThankYou from "@/components/ThankYou";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Wordmark = () => (
  <div className="flex flex-col leading-none" data-testid="wordmark">
    <span className="font-wordmark font-extrabold tracking-tight text-white text-xl">
      GREENBOOKS
    </span>
    <span className="font-wordmark font-medium text-[10px] tracking-[0.32em] text-[#A8E0BE] mt-1">
      CONSULTING&nbsp;INC.
    </span>
  </div>
);

const Header = ({ clientName }) => (
  <header
    className="px-5 pt-6 pb-7 text-white"
    style={{ background: "linear-gradient(135deg, #0B3D2E 0%, #16784A 100%)" }}
    data-testid="portal-header"
  >
    <div className="flex items-start justify-between">
      <Wordmark />
      <span className="italic text-[11px] text-[#A8E0BE] max-w-[150px] text-right leading-snug mt-1">
        Netting Your Bottom Line Through Automation
      </span>
    </div>
    {clientName && (
      <div className="mt-6">
        <h1 className="text-2xl font-wordmark font-bold" data-testid="greeting">
          Hi {clientName} 👋
        </h1>
        <p className="text-[14px] text-white/85 mt-2 leading-relaxed">
          Below are some transactions we couldn't categorize. Please add a short
          clarification on each so we can update your books accordingly. Thank you!
        </p>
      </div>
    )}
  </header>
);

const LoadingScreen = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#eef4f1]" data-testid="loading-screen">
    <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#16a34a" }} />
    <p className="mt-3 text-sm text-gray-500">Loading your portal…</p>
  </div>
);

const InvalidScreen = () => (
  <div className="min-h-screen flex flex-col" data-testid="invalid-screen">
    <Header />
    <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
      <div className="w-16 h-16 rounded-full flex items-center justify-center bg-white shadow-md">
        <WifiOff className="w-7 h-7 text-[#b4432f]" />
      </div>
      <h2 className="mt-5 text-lg font-wordmark font-bold text-[#0B3D2E]">
        This link isn't active
      </h2>
      <p className="mt-2 text-sm text-gray-600 max-w-xs">
        Please contact Greenbooks and we'll send you a fresh link.
      </p>
    </div>
  </div>
);

export default function Portal() {
  const [status, setStatus] = useState("loading"); // loading | invalid | ready | done
  const [clientName, setClientName] = useState("");
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({}); // recordId -> {category, purpose, vendor, tax, files}
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [sentCount, setSentCount] = useState(0);
  const tokenRef = useRef("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let token = (params.get("c") || "").trim();
    // Fallback 1: hash (some hosts preserve hash but drop query on redirect)
    if (!token) {
      const hm = (window.location.hash || "").match(/[?&#]c=([^&#]+)/);
      if (hm && hm[1]) token = decodeURIComponent(hm[1]).trim();
    }
    // Fallback 2: value captured by the early inline script in index.html
    if (!token) {
      try {
        token = (sessionStorage.getItem("gb_c") || "").trim();
      } catch (e) {}
    }
    // Persist so a later redirect that drops the query can't lose it
    if (token) {
      try {
        sessionStorage.setItem("gb_c", token);
      } catch (e) {}
    }
    tokenRef.current = token;
    if (!token) {
      setStatus("invalid");
      return;
    }
    axios
      .get(`${API}/client`, { params: { c: token } })
      .then((res) => {
        if (!res.data.valid) {
          setStatus("invalid");
          return;
        }
        setClientName(res.data.clientName);
        setItems(res.data.items || []);
        setStatus("ready");
      })
      .catch(() => setStatus("invalid"));
  }, []);

  const updateAnswer = (recordId, patch) => {
    setAnswers((prev) => ({
      ...prev,
      [recordId]: { ...(prev[recordId] || {}), ...patch },
    }));
  };

  const total = items.length;
  const done = useMemo(
    () => items.filter((it) => answers[it.recordId]?.category).length,
    [items, answers]
  );
  const pct = total ? Math.round((done / total) * 100) : 0;

  const buildPayload = () => {
    const list = [];
    for (const it of items) {
      const a = answers[it.recordId];
      if (!a?.category) continue;
      list.push({
        recordId: it.recordId,
        category: a.category,
        purpose: a.purpose || "",
        vendor: a.vendor || "",
        tax: a.tax || "",
        files: (a.files || []).map((f) => ({
          filename: f.filename,
          contentType: f.contentType,
          base64: f.base64,
        })),
      });
    }
    return list;
  };

  const handleSubmit = async () => {
    if (done < 1 || submitting) return;
    setErrorMsg("");
    setSubmitting(true);
    const payload = { token: tokenRef.current, answers: buildPayload() };

    const attempt = () => axios.post(`${API}/answers`, payload, { timeout: 60000 });

    try {
      let res;
      try {
        res = await attempt();
      } catch (e) {
        // auto-retry once for weak mobile signal
        res = await attempt();
      }
      if (res?.data?.success) {
        setSentCount(res.data.count || done);
        setStatus("done");
      } else {
        throw new Error("no success");
      }
    } catch (e) {
      setErrorMsg("Couldn't send — check your connection and tap again");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") return <LoadingScreen />;
  if (status === "invalid") return <InvalidScreen />;
  if (status === "done") return <ThankYou count={sentCount} />;

  const buttonLabel =
    done === total && total > 0
      ? `Send all ${total} answers ✓`
      : `Send my ${done} answer${done === 1 ? "" : "s"}`;

  return (
    <div className="min-h-screen bg-[#eef4f1] pb-28" data-testid="portal">
      <Header clientName={clientName} />

      {/* Sticky progress */}
      <div className="sticky top-0 z-20 bg-[#eef4f1]/95 backdrop-blur px-5 py-3 border-b border-black/5" data-testid="progress-bar">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-[#0B3D2E]">Your progress</span>
          <span className="text-xs font-semibold text-[#16784A]" data-testid="progress-count">
            {done} of {total} done
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-[#d5e6dd] overflow-hidden">
          <div
            className="h-full rounded-full gb-progress-fill"
            style={{ width: `${pct}%`, backgroundColor: "#16a34a" }}
            data-testid="progress-fill"
          />
        </div>
      </div>

      {/* Cards */}
      <div className="px-4 pt-4 space-y-4" data-testid="transaction-list">
        {items.length === 0 && (
          <div className="bg-white rounded-2xl p-6 text-center shadow-sm mt-6">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#16a34a]" />
            <p className="mt-3 text-sm text-gray-600">
              You're all caught up — nothing needs clarifying right now.
            </p>
          </div>
        )}
        {items.map((it, idx) => (
          <TransactionCard
            key={it.recordId}
            item={it}
            index={idx}
            answer={answers[it.recordId] || {}}
            onChange={(patch) => updateAnswer(it.recordId, patch)}
          />
        ))}
      </div>

      {/* Fixed bottom button */}
      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 px-4 pb-4 pt-3 bg-gradient-to-t from-[#eef4f1] via-[#eef4f1] to-transparent" data-testid="submit-bar">
          {errorMsg && (
            <p className="text-center text-[13px] text-[#b4432f] mb-2 font-medium" data-testid="submit-error">
              {errorMsg}
            </p>
          )}
          <button
            onClick={handleSubmit}
            disabled={done < 1 || submitting}
            data-testid="submit-button"
            className="w-full h-14 rounded-2xl font-wordmark font-bold text-white text-base gb-transition flex items-center justify-center gap-2 shadow-lg disabled:opacity-40 active:scale-[0.98]"
            style={{ backgroundColor: done < 1 ? "#9db8ab" : "#16a34a" }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Sending…
              </>
            ) : (
              buttonLabel
            )}
          </button>
        </div>
      )}
    </div>
  );
}
